/**
 * Dice — the pure, foundational dice module for combat resolution.
 *
 * Rolls are **structured descriptors** (`{ count, sides, keep?, accuracy?, crit? }`),
 * not a `"2d6kh1+3"` string DSL: no user types raw dice in the resolution flow, so a
 * descriptor the resolver builds programmatically and that serializes straight into a
 * command is the right shape. The roll capabilities cover what Lancer attack/damage
 * needs: d20, NdM, accuracy/difficulty (keep-highest among extra d6, cancel 1:1),
 * crit-doubling, and overkill reroll (reroll 1s).
 *
 * Everything here is **pure** — no `Math.random`, no I/O. Randomness enters through an
 * injected {@link DiceSource}, and every individual die draw is **returned as data** in
 * the {@link RollResult}. This is load-bearing for the wider architecture: the
 * authority's `resolve` pass draws-and-records, and the recorded draws are what make a
 * fight replayable and version-stable, so the dice layer must surface its draws as
 * outcomes, never hide them behind RNG. A seeded or fully-scripted source can therefore
 * drive resolution deterministically for tests and replay.
 *
 * This lives in `@lancer-vtt/rules` (the authority-side resolution layer that will own
 * the combat math + spawn adapters), next to — not inside — the pure reducer in
 * `@lancer-vtt/domain`. It has no `domain`/`compendium` dependency: it is leaf math the
 * resolver consumes.
 */

// ── Dice sources ────────────────────────────────────────────────────────────────

/**
 * The injectable randomness seam. A source yields one die roll in `[1, sides]` per
 * call. Resolution injects a source so every draw is sourced through one chokepoint
 * the resolver can record; tests and replay inject deterministic sources.
 *
 * Implementations must return an integer in `[1, sides]` for `sides >= 1`. The dice
 * functions below treat the source as a stream and never inspect `sides` themselves
 * beyond passing it through — the source owns the range.
 */
export type DiceSource = {
  /** Roll a single die with the given number of sides; returns an integer in `[1, sides]`. */
  roll(sides: number): number;
}

/**
 * A real, non-deterministic source backed by a supplied `random()` (defaults to
 * `Math.random`). The `random` injection keeps even this "live" source seedable —
 * pass a seeded PRNG's `next` to get a reproducible-but-realistic stream.
 *
 * This is the only place a raw random number is consumed; the rest of the module is
 * pure given a source.
 */
export function randomDiceSource(random: () => number = Math.random): DiceSource {
  return {
    roll(sides: number): number {
      if (sides < 1 || !Number.isInteger(sides)) {
        throw new Error(`randomDiceSource: invalid sides ${sides}`);
      }
      return Math.floor(random() * sides) + 1;
    },
  };
}

/**
 * Thrown by {@link scriptedDiceSource} when a draw runs past the end of its script. A class of
 * its own so a boundary (the encounter room's resolve site, fed by the backend's `LVTT_DICE`
 * stream) can report an under-scripted run by name instead of letting it read as a crash or a
 * silent no-op.
 */
export class DiceExhaustedError extends Error {
  constructor(readonly draws: number) {
    super(`scriptedDiceSource: exhausted after ${draws} draw(s) — fixture under-scripted its dice`);
    this.name = "DiceExhaustedError";
  }
}

/**
 * A fully deterministic source that replays a **scripted** list of die values in
 * order. This is the test/replay driver: hand it exactly the draws a fixture expects
 * and the dice math becomes hand-checkable.
 *
 * It records nothing itself — recording is the resolver's job over the
 * {@link RollResult}s; this just feeds known values. Two fixture bugs throw rather than
 * silently returning a wrong value: drawing past the end of the script (under-scripted
 * dice), and a face the die being rolled cannot show (a 15 on a d6), which would
 * otherwise let a test pass on an outcome no table could produce.
 */
export function scriptedDiceSource(values: readonly number[]): DiceSource {
  let i = 0;
  return {
    roll(sides: number): number {
      if (i >= values.length) {
        throw new DiceExhaustedError(values.length);
      }
      const face = values[i]!;
      if (!Number.isInteger(face) || face < 1 || face > sides) {
        throw new Error(`scriptedDiceSource: draw ${i + 1} is ${face}, which a d${sides} cannot roll (faces 1-${sides})`);
      }
      i++;
      return face;
    },
  };
}

/**
 * A constant {@link DiceSource} for DISCOVERY-ONLY probes — `previewAttackDeclaredChoice`
 * (`packages/rules/src/encounter/resolve.ts`) is the sole intended caller. Every `roll()`
 * call returns the same fixed face (clamped into `[1, sides]`, so the {@link DiceSource}
 * contract is honored for whatever die size a clause happens to draw) — it never exhausts
 * (unlike {@link scriptedDiceSource}), so a dice-drawing atom the probe walks through
 * (e.g. `t_siege_specialist:2`'s declared-phase `force_save`) crosses cleanly instead of
 * throwing.
 *
 * **Discovery-only. Its values are never read into any output.** The probe this backs
 * discards `steps` entirely and returns only the halt signal (`promptId`/`options`) — so
 * whatever face this source yields cannot leak into a broadcast, a recorded resolution
 * step, or persisted state. Never wire this into a live or recording resolution path — if
 * a draw from here ever needs to reach an output, that is a sign this function was reused
 * somewhere it shouldn't be, not a sign the constant should change.
 *
 * The chosen face (`10`, clamped to the requested `sides`) is a deliberately unremarkable
 * mid-range value — not `1` (reads as "every save auto-fails") and not the max (reads as
 * "every save auto-passes" / "every roll auto-crits"). It doesn't matter for any shipped
 * content: the one declared-phase dice-drawer in the corpus (`t_siege_specialist:2`'s
 * `force_save`) has no `choose` in either its `on_fail` or `on_pass` branch, so which
 * branch the probe takes never changes what it discovers. **But an authored clause COULD
 * nest a `choose` behind a `force_save`'s pass/fail or a `roll`'s hit/miss branch** — at
 * which point discovery would silently start depending on this constant's face. The corpus
 * pin (`packs/core-rulebook/entries/_helpers/declaredDiceChooseNesting.pin.test.ts`) fails
 * the day a shipped clause takes that shape, so the luck runs out loudly rather than as a
 * coin-flip bug report.
 *
 * **Don't substitute {@link seededDiceSource}.** It is also deterministic and
 * non-exhausting, but it exists to produce a REALISTIC-looking pseudo-random stream that
 * varies every draw — reusing it here would need an arbitrary seed with no meaning at the
 * call site, and would blur the "provably inert, only ever crossed and discarded" line the
 * discovery-only contract depends on. A dedicated, argument-less, constant source makes
 * "this can never be a real roll" visible in the name itself.
 */
export function projectionDiceSource(): DiceSource {
  const FACE = 10;
  return {
    roll(sides: number): number {
      if (sides < 1 || !Number.isInteger(sides)) {
        throw new Error(`projectionDiceSource: invalid sides ${sides}`);
      }
      return Math.min(FACE, sides);
    },
  };
}

/**
 * A {@link DiceSource} that **replays a recorded prefix, records everything, and draws
 * fresh past the prefix** — the monotonic recorded-input log that makes a *paused*
 * resolution stable across the prompt round-trip.
 *
 * The prompt round-trip is **iterate-pure**: a resolution that hits an unanswered `choose`
 * returns `needs_decision`, the client records one more answer, and the authority
 * **re-runs the whole resolution from the top**. Recording only the *choices* suffices
 * while every prompt sits *before* any dice it depends on. It does not for a prompt raised
 * AFTER dice are rolled — a defender's Brace reaction, or Lock On's consume/keep prompt
 * after damage — where the re-run would re-draw those dice and land different numbers.
 * This source closes that gap.
 *
 * It is a {@link DiceSource} over an underlying `fresh` source: each `roll` either
 * **replays** the next value of `recorded` (in draw order, regardless of `sides` — the
 * recorded value is trusted: it was a real draw when it was logged) or, once the prefix is
 * exhausted, **draws from `fresh`**. *Every* yielded value (replayed or fresh) is appended
 * to {@link RecordingDiceSource.log}. So after a resolution halts with `needs_decision`,
 * `log()` is the full dice-so-far the authority rides back in the intent; the answered
 * re-run feeds that log back as `recorded` and replays it, reproducing the exact numbers
 * the resolution paused on, before continuing past the (now-answered) choice with fresh
 * draws.
 *
 * **One instance per halt-capable chain, and never a second over the same log.** The scope of a
 * recording source is the whole chain that can pause, not one resolver: it is built at the chain's
 * entry point and passed down as the ctx's `dice` for the entire walk (`decisions/0164` Decision 1
 * — `backend/src/encounterRoomCore.ts`'s `applyHookedPlan` and
 * `packages/rules/src/encounter/referee/refereeMove.ts` are the two entry points that do this
 * today). Wrapping an already-wrapped source over the same `recorded` array is the failure mode to
 * watch for: the inner instance restarts at index 0 and replays the outer's prefix into whatever
 * runs downstream, so a hazard gets the checkpoint's dice instead of its own. It throws nothing and
 * warns nothing — the only symptom is a different outcome than the one the player was shown. The
 * pin is `referee/moveChainRecordingDice.test.ts`.
 *
 * **Why a monotonic positional log is correct:** resolution is pure over `(state, intent,
 * recordedInputLog)`, so the draw *order* before a `needs_decision` halt is fixed — the
 * new answer only affects control flow *at and after* the halt, never the dice drawn
 * before it. The re-run therefore draws the same sequence up to the prefix length, so a
 * bare draw-order index is a stable key (no semantic keying needed). Across multiple
 * round-trips the log grows monotonically (prior prefix + this round's fresh draws).
 */
export type RecordingDiceSource = DiceSource & {
  /** The monotonic log of every value yielded so far (replayed + fresh), in draw order. */
  readonly log: () => number[];
};

/**
 * Build a {@link RecordingDiceSource} that replays `recorded` (in order) then draws from
 * `fresh`, recording all yielded values. Pass `[]` for `recorded` on a fresh resolution
 * (records only); pass the dice-so-far on a re-resolve to replay them. The `fresh` source
 * is the authority's real dice (random in production, scripted in a test).
 */
export function recordingDiceSource(
  recorded: readonly number[],
  fresh: DiceSource,
): RecordingDiceSource {
  const drawn: number[] = [];
  let i = 0;
  return {
    roll(sides: number): number {
      const value = i < recorded.length ? recorded[i]! : fresh.roll(sides);
      i++;
      drawn.push(value);
      return value;
    },
    log: () => [...drawn],
  };
}

/**
 * Build a {@link DiceSource} that draws from a tiny deterministic LCG (Numerical Recipes
 * constants), seeded from a plain numeric `seed` — the SAME reproducible-PRNG-over-
 * `randomDiceSource` pattern `backend/src/index.ts`'s `makeDiceFactory` already uses for
 * `LVTT_DICE_SEED` (an acceptance-run convenience), **copied here rather than imported
 * because `packages/ai`/`packages/rules` must stay backend-free**. Two sources built from
 * the SAME `seed` draw the identical sequence forever (never exhausts, unlike
 * {@link scriptedDiceSource}) — the byte-reproducibility primitive the autoresolution
 * direct-referee match handle needs.
 */
export function seededDiceSource(seed: number): DiceSource {
  let state = seed >>> 0;
  const random = (): number => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
  return randomDiceSource(random);
}

// ── Descriptors + results ───────────────────────────────────────────────────────

/**
 * A structured roll descriptor. The resolver builds these programmatically and they
 * serialize straight into a command.
 *
 * - `count` / `sides` — roll `count` dice of `sides` faces (`count` d20 or NdM).
 * - `keep` — keep-highest: after rolling, keep the `keep` highest dice and sum those
 *   (e.g. exploding the base die pool). Omitted = keep all.
 * - `accuracy` — net accuracy/difficulty applied to a **single base d20** roll
 *   (`{ count: 1, sides: 20 }`): positive = accuracy, negative = difficulty. Each
 *   point adds one extra d6; the net is `+max(extra d6)` for accuracy or
 *   `-max(extra d6)` for difficulty (acc and diff cancel 1:1 before getting here, but
 *   see {@link netAccuracy}). Omitted/0 = no extra dice.
 * - `crit` — crit-doubling: roll `count * 2` dice and keep the `count` highest, then
 *   sum (Lancer crit = roll double the damage dice, keep the better half). Omitted =
 *   no crit.
 *
 * The fields compose, but in practice a descriptor is built for one job (a d20
 * to-hit with `accuracy`, or an NdM damage roll with `crit`/`keep`).
 */
export type DiceDescriptor = {
  count: number;
  sides: number;
  keep?: number;
  accuracy?: number;
  crit?: boolean;
}

/** A single recorded die draw: its face count and the value rolled. */
export type DieDraw = {
  sides: number;
  value: number;
}

/**
 * The outcome of rolling a descriptor — **all draws recorded as data**, plus the
 * computed total. The `draws` array is the audit trail the resolver records into its
 * resolved step list; `kept` flags which draws contributed to `total`
 * (the rest were dropped by keep-highest / accuracy / crit).
 */
export type RollResult = {
  /** The descriptor this result came from (echoed for the recorded step). */
  descriptor: DiceDescriptor;
  /** Every die drawn, in draw order. */
  draws: DieDraw[];
  /** Parallel to `draws`: whether each draw was kept (true) or dropped (false). */
  kept: boolean[];
  /** The accuracy/difficulty net die contribution, if any (signed; +acc / -diff). */
  accuracyMod?: number;
  /** The final summed total. */
  total: number;
}

// ── Accuracy / difficulty ────────────────────────────────────────────────────────

/**
 * Net an accuracy/difficulty pool: accuracy and difficulty cancel **1:1**, leaving a
 * signed count (`+` accuracy, `-` difficulty, `0` none). Lancer never stacks more than
 * one *net* die's worth of effect per point, but multiple points still each add a die —
 * the netting here is only the accuracy-vs-difficulty cancellation, not a cap.
 *
 * @param accuracy number of accuracy points (>= 0)
 * @param difficulty number of difficulty points (>= 0)
 * @returns signed net: positive = net accuracy, negative = net difficulty
 */
export function netAccuracy(accuracy: number, difficulty: number): number {
  return accuracy - difficulty;
}

// ── Core roller ──────────────────────────────────────────────────────────────────

function rollDice(source: DiceSource, count: number, sides: number): DieDraw[] {
  if (count < 0 || !Number.isInteger(count)) {
    throw new Error(`rollDice: invalid count ${count}`);
  }
  const draws: DieDraw[] = [];
  for (let n = 0; n < count; n++) {
    draws.push({ sides, value: source.roll(sides) });
  }
  return draws;
}

/**
 * Mark the `keep` highest of `draws` as kept (the rest dropped), returning a `kept[]`
 * parallel to `draws`. Ties are broken by draw order (earlier draws kept first) so the
 * behavior is deterministic. `keep >= draws.length` keeps everything.
 */
function keepHighest(draws: readonly DieDraw[], keep: number): boolean[] {
  const kept = new Array<boolean>(draws.length).fill(false);
  if (keep >= draws.length) {
    return kept.fill(true);
  }
  // Indices sorted by value desc, draw-order asc on ties.
  const order = draws
    .map((d, idx) => ({ idx, value: d.value }))
    .sort((a, b) => b.value - a.value || a.idx - b.idx);
  for (let k = 0; k < keep; k++) {
    kept[order[k]!.idx] = true;
  }
  return kept;
}

function sumKept(draws: readonly DieDraw[], kept: readonly boolean[]): number {
  let total = 0;
  for (let i = 0; i < draws.length; i++) {
    if (kept[i]) total += draws[i]!.value;
  }
  return total;
}

/**
 * Roll a {@link DiceDescriptor} through a {@link DiceSource}, recording every draw.
 *
 * Resolution of the descriptor's fields:
 * 1. **Crit** (`crit: true`) doubles the dice pool — roll `count * 2` dice and keep the
 *    `count` highest (Lancer crit = roll double, keep the better half). `crit` and an
 *    explicit `keep` are mutually exclusive (crit *defines* the keep); passing both
 *    throws.
 * 2. Otherwise roll `count` dice; if `keep` is set, keep the `keep` highest, else keep
 *    all.
 * 3. **Accuracy** (`accuracy`, only meaningful on a single base die) rolls `|accuracy|`
 *    extra d6 and adds the single highest to the total (`+` for accuracy, `-` for
 *    difficulty). The extra d6 are recorded as draws too, but only the contributing one
 *    is marked kept.
 *
 * @throws if both `keep` and `crit` are set, or on a malformed descriptor.
 */
export function roll(descriptor: DiceDescriptor, source: DiceSource): RollResult {
  const { count, sides, keep, accuracy, crit } = descriptor;
  if (count < 0 || !Number.isInteger(count) || sides < 1 || !Number.isInteger(sides)) {
    throw new Error(`roll: malformed descriptor (count=${count}, sides=${sides})`);
  }
  if (crit && keep !== undefined) {
    throw new Error("roll: `crit` and `keep` are mutually exclusive (crit defines the keep)");
  }

  let draws: DieDraw[];
  let kept: boolean[];

  if (crit) {
    draws = rollDice(source, count * 2, sides);
    kept = keepHighest(draws, count);
  } else if (keep !== undefined) {
    if (keep < 0 || !Number.isInteger(keep)) {
      throw new Error(`roll: invalid keep ${keep}`);
    }
    draws = rollDice(source, count, sides);
    kept = keepHighest(draws, keep);
  } else {
    draws = rollDice(source, count, sides);
    kept = new Array<boolean>(draws.length).fill(true);
  }

  let total = sumKept(draws, kept);
  let accuracyMod: number | undefined;

  if (accuracy !== undefined && accuracy !== 0) {
    const extra = Math.abs(accuracy);
    const accDraws = rollDice(source, extra, 6);
    // Keep the single highest extra d6; its value is the magnitude of the modifier.
    const accKept = keepHighest(accDraws, 1);
    const magnitude = sumKept(accDraws, accKept);
    accuracyMod = accuracy > 0 ? magnitude : -magnitude;
    total += accuracyMod;
    // Append the accuracy dice to the recorded draws (the kept one flagged true).
    for (let i = 0; i < accDraws.length; i++) {
      draws.push(accDraws[i]!);
      kept.push(accKept[i]!);
    }
  }

  return { descriptor, draws, kept, accuracyMod, total };
}

// ── Convenience builders ─────────────────────────────────────────────────────────

/**
 * Roll a single d20 with an optional net accuracy/difficulty (`acc` signed: `+`
 * accuracy, `-` difficulty). The Lancer attack roll's dice half — the resolver adds the
 * flat attack bonus to `total` itself.
 *
 * `advantage` (an attack-roll replacement — t_spotter:1's Particularize: "…roll twice and
 * choose either result") rolls TWO base d20s and keeps
 * the higher (`count: 2, keep: 1`) instead of one — the existing `roll()` descriptor
 * shape generalizes to this with no new dice-module mechanism; the accuracy pool (if any)
 * still adds on top exactly as it does for a single base die. Both base draws land in
 * `RollResult.draws` (only the kept one flagged `true`), so the caller can witness/record
 * the dropped die too. Defaults `false` — a single d20.
 */
export function rollD20(source: DiceSource, acc = 0, advantage = false): RollResult {
  return roll(advantage ? { count: 2, sides: 20, keep: 1, accuracy: acc } : { count: 1, sides: 20, accuracy: acc }, source);
}

/**
 * Roll `count`d`sides`, optionally crit-doubled (keep the better half). The damage
 * roll's dice half — the resolver applies typed mitigation (armor/AP/resist) to the
 * `total` afterward.
 */
type RollDamageArgs = {
  source: DiceSource;
  count: number;
  sides: number;
  crit?: boolean;
};

export function rollDamage(args: RollDamageArgs): RollResult {
  const { source, count, sides, crit = false } = args;
  return roll({ count, sides, crit }, source);
}

// ── Overkill ─────────────────────────────────────────────────────────────────────

/** The recorded outcome of an overkill reroll pass over one die. */
export type OverkillReroll = {
  /** Every value this die showed, in order: the original then each reroll. */
  values: number[];
  /** The final (non-1) value this die settled on. */
  final: number;
  /** How many rerolls happened (= heat the wielder takes for this die). */
  rerolls: number;
}

/**
 * Roll a damage pool with **overkill**: any die that comes up `1` is rerolled (and a
 * `1` on the reroll rerolls again, ad infinitum), and each reroll deals 1 heat to the
 * wielder. Returns per-die reroll records (so the resolver can sum heat and record the
 * full draw history) plus the kept total.
 *
 * Unlike {@link roll}, overkill needs reroll loops, so it's a dedicated function rather
 * than a descriptor field. The `sides`-faced dice are drawn through the same source, so
 * a scripted source must supply enough values to satisfy the rerolls (e.g. for a die
 * that rolls `1, 1, 4`, script `[1, 1, 4]`).
 *
 * @returns `{ total, heat, dice }` — kept damage total, total heat (= total rerolls),
 *   and the per-die reroll trail.
 */
export function rollOverkill(
  source: DiceSource,
  count: number,
  sides: number,
): { total: number; heat: number; dice: OverkillReroll[] } {
  if (count < 0 || !Number.isInteger(count) || sides < 1 || !Number.isInteger(sides)) {
    throw new Error(`rollOverkill: malformed (count=${count}, sides=${sides})`);
  }
  const dice: OverkillReroll[] = [];
  let total = 0;
  let heat = 0;
  for (let n = 0; n < count; n++) {
    const values: number[] = [];
    let value = source.roll(sides);
    values.push(value);
    let rerolls = 0;
    while (value === 1) {
      value = source.roll(sides);
      values.push(value);
      rerolls++;
    }
    dice.push({ values, final: value, rerolls });
    total += value;
    heat += rerolls;
  }
  return { total, heat, dice };
}
